<script>
export default {
  name: 'CpImagesCarousel',
  components: {
    CpButton: require('../button').default,
    CpButtonLink: require('../button-link').default,
    handleImageError: require('../mixins/utils').default,
  },
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginBottom: require('../directives/margins').marginBottom,
  },
  props: {
    carouselItems: { type: Array, default: [] },
  },
  data() {
    return {
      totalPages: 0,
      currentPage: 0,
      resizeObserver: null,
      isScrollingProgrammatically: false,
    };
  },
  mounted() {
    if (this.carouselItems.length) {
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer;
        if (!container) return;

        this.calculateTotalPages();

        this.resizeObserver = new ResizeObserver(() => {
          this.calculateTotalPages();
        });
        this.resizeObserver.observe(container);

        container.addEventListener('scroll', this.handleScroll, { passive: true });
      });
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    const container = this.$refs.scrollContainer;
    if (container) {
      container.removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    calculateTotalPages() {
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer;
        if (!container) return;

        const containerWidth = container.offsetWidth;
        const scrollWidth = container.scrollWidth;
        this.totalPages = Math.ceil(scrollWidth / containerWidth);
      });
    },
    handleScroll() {
      if (this.isScrollingProgrammatically) return;

      const container = this.$refs.scrollContainer;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      const containerWidth = container.offsetWidth;
      const scrollWidth = container.scrollWidth;
      const maxScrollLeft = scrollWidth - containerWidth;
      const scrollProgress = scrollLeft / maxScrollLeft;

      const currentPage = Math.round(scrollProgress * (this.totalPages - 1));

      if (currentPage !== this.currentPage) {
        this.currentPage = currentPage;
      }
    },
    scrollToPage(pageIndex) {
      const container = this.$refs.scrollContainer;
      if (!container) return;

      this.isScrollingProgrammatically = true;

      const containerWidth = container.offsetWidth;
      const scrollWidth = container.scrollWidth;
      const maxScrollLeft = scrollWidth - containerWidth;

      const targetScrollLeft = (pageIndex / (this.totalPages - 1)) * maxScrollLeft;

      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });

      setTimeout(() => {
        this.currentPage = pageIndex;
        this.isScrollingProgrammatically = false;
      }, 500);
    },
    redirectTo(path) {
      window.location.href = path;
    },
  },
};
</script>

<template>
  <div>
    <div :class="['carousel', { 'centered': carouselItems.length < 4 }]" ref="scrollContainer">
      <div class="card" v-for="(item) in carouselItems" :key="item.id" @click="redirectTo(item.permalink)">
        <img :src="`${item.image ?? item.large_img}`" alt="Official's image" @error="handleImageError" loading="lazy" />
        <p :class="{ buttons: !$root.isMobile, caption: $root.isMobile, 'neutral6-color': true }" v-margin-bottom="0">{{ item.name ?? item.full_name }}</p>
        <p v-if="item.officeName" class="footnote neutral6-color" v-margin-top="0">{{ item.officeName }}</p>
      </div>
    </div>
    <div v-if="totalPages > 1" class="dots">
      <span v-for="(dot, i) in totalPages" :key="i" :class="{ active: i === currentPage }" @click="scrollToPage(i)"></span>
    </div>
  </div>
</template>

<style scoped>
.carousel {
  display: flex;
  overflow-x: scroll;
  padding-bottom: 20px;
}
.carousel.centered {
  justify-content: center;
}
.card {
  display: flex;
  flex-direction: column;
  width: 200px;
  border: 1px solid var(--neutral3);
  border-radius: 6px;
  margin: 0 5px;
  cursor: pointer;
}
.card:first-child {
  margin-left: 0;
}
.card:last-child {
  margin-right: 0;
}
.card img {
  width: 200px;
  height: 150px;
  object-fit: cover;
}
.card p {
  padding: 0 16px;
}
.dots {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}
.dots span {
  width: 12px;
  height: 12px;
  margin: 0 4px;
  border-radius: 50%;
  background: var(--neutral3);
  cursor: pointer;
}
.dots .active {
  background: var(--primary-orange);
}
@media screen and (max-width: 768px) {
  .card {
    width: 150px;
  }
  .card img {
    width: 150px;
    height: 100px;
  }
}
@media screen and (max-width: 500px) {
  .card {
    width: 100px;
  }
  .card img {
    width: 100px;
    height: 72px;
  }
}
</style>
